// Copied from mini-apps/launcher/utils/dates.ts when Playbooks was extracted
// (sibling imports are banned; a 2nd copy of a small helper is tolerated —
// promote to _shared at the 3rd).
// Date helpers that avoid the `new Date("YYYY-MM-DD")` UTC-parse pitfall.
//
// JS parses a bare date-only string ("2026-10-07") as UTC midnight, which then
// renders as the *previous* day in negative-offset (US) timezones — e.g. an
// event stored as Oct 7 shows as Oct 6. These helpers parse date-only strings
// at LOCAL midnight. Full ISO timestamps (containing a 'T') are real instants
// and are parsed normally.

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse a date string to a Date. Date-only strings are parsed at LOCAL midnight. */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = DATE_ONLY.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Format a date-only (or ISO) string for display without timezone shift. */
export function formatEventDate(
  value: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string | null {
  const d = parseDateOnly(value);
  return d ? d.toLocaleDateString('en-US', opts) : null;
}

/** Local "today" as YYYY-MM-DD (not UTC — avoids off-by-one near midnight). */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Whole days from local-midnight-today to the given date-only string (event date). */
export function daysUntil(value: string | null | undefined): number | null {
  const target = parseDateOnly(value);
  if (!target) return null;
  const today = parseDateOnly(todayIso());
  if (!today) return null;
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}
